import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { TbBrowser, TbDeviceDesktop, TbInfoCircle } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { SyncGuide, type GuideJumpTarget } from "./guide/SyncGuide";
import "./syncHowItWorks.scss";

type Method = "browser" | "agent";

const FACTS = ["covers", "needs", "runs"] as const;
const METHODS: { key: Method; icon: React.ReactNode }[] = [
    { key: "browser", icon: <TbBrowser /> },
    { key: "agent", icon: <TbDeviceDesktop /> },
];

const SEEN_KEY = (m: Method) => `pvsSyncGuideSeen:${m}`;

/** Whether this browser has already shown the setup guide for a method — a per-viewer convenience only, so the
 *  guide opens by itself the first time someone lands on a method and stays out of the way after that.
 *  Unreadable storage (private mode, blocked) counts as seen, so nobody gets the popup on every visit. */
export function hasSeenSyncGuide(m: Method): boolean {
    try {
        return localStorage.getItem(SEEN_KEY(m)) === "1";
    } catch {
        return true;
    }
}

export function markSyncGuideSeen(methods: Method[]): void {
    try {
        methods.forEach((m) => localStorage.setItem(SEEN_KEY(m), "1"));
    } catch {
        // storage blocked — it just isn't remembered
    }
}

/** "How it works" for the two ways to sync patient data — reading the export folder in the browser (File
 *  System Access API, Chromium only) or the Windows desktop agent. One method at a time (a tab switch): what
 *  it covers and needs at a glance, then the full step-by-step setup (guide/SyncGuide.tsx).
 *  Opens with a method-agnostic "what is this" chapter — someone opening it may never have used the feature
 *  at all. onClose reports which methods' guides were actually shown, so the page can stop auto-opening them.
 *  What each path does and doesn't cover mirrors info/Sync/praxis-software-sync-agent-runbook.md. */
export function SyncHowItWorks({ onClose, onJump, initial = "browser" }: {
    onClose: (viewed: Method[]) => void;
    onJump?: (method: Method, target: GuideJumpTarget) => void;
    initial?: Method;
}) {
    const { t } = useTranslation();
    const [method, setMethod] = useState<Method>(initial);
    const viewed = useRef(new Set<Method>([initial]));
    const panelRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);
    const firstRender = useRef(true);

    useEffect(() => {
        viewed.current.add(method);
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        // Switching tabs deep inside the scrolled popup should start the new method from its top —
        // just below the pinned tabs/facts block, whose height depends on the method's fact texts.
        const panel = panelRef.current;
        if (!panel) return;
        const sticky = stickyRef.current;
        const pinned = sticky && getComputedStyle(sticky).position === "sticky" ? sticky.offsetHeight : 0;
        panel.style.scrollMarginTop = `${pinned}px`;
        panel.scrollIntoView({ block: "nearest" });
    }, [method]);

    const close = () => onClose([...viewed.current]);

    const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") return;
        e.preventDefault();
        const next: Method = e.key === "Home" ? "browser" : e.key === "End" ? "agent" : method === "browser" ? "agent" : "browser";
        setMethod(next);
        document.getElementById(`sync-how-tab-${next}`)?.focus();
    };

    return (
        <Popup
            className="popup--xl"
            title={t("syncHowItWorks.title")}
            onClose={close}
            actions={[{ label: t("syncHowItWorks.done"), variant: "primary", onClick: close }]}
            message={
                <div className="sync-how">
                    <div className="sync-how__about">
                        <h3 className="sync-how__about-title">{t("syncHowItWorks.aboutTitle")}</h3>
                        <p className="sync-how__intro">{t("syncHowItWorks.about")}</p>
                    </div>

                    {/* Method switch and its at-a-glance facts stay pinned while the long guide scrolls under them. */}
                    <div ref={stickyRef} className="sync-how__sticky">
                        <div className="sync-how__tabs" role="tablist" aria-label={t("syncHowItWorks.tabsLabel")}>
                            {METHODS.map(({ key, icon }) => (
                                <button
                                    key={key}
                                    type="button"
                                    role="tab"
                                    id={`sync-how-tab-${key}`}
                                    aria-selected={method === key}
                                    aria-controls={`sync-how-panel-${key}`}
                                    tabIndex={method === key ? 0 : -1}
                                    className={`sync-how__tab${method === key ? " sync-how__tab--active" : ""}`}
                                    onClick={() => setMethod(key)}
                                    onKeyDown={onTabKey}
                                >
                                    {icon}
                                    <span>{t(`syncHowItWorks.${key}.tab`)}</span>
                                </button>
                            ))}
                        </div>

                        <dl className="sync-how__facts">
                            {FACTS.map((f) => (
                                <div key={f} className="sync-how__fact">
                                    <dt>{t(`syncHowItWorks.facts${f[0].toUpperCase()}${f.slice(1)}`)}</dt>
                                    <dd>{t(`syncHowItWorks.${method}.facts.${f}`)}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div
                        key={method}
                        ref={panelRef}
                        className="sync-how__panel"
                        role="tabpanel"
                        id={`sync-how-panel-${method}`}
                        aria-labelledby={`sync-how-tab-${method}`}
                    >
                        <SyncGuide method={method} onJump={onJump ? (target) => onJump(method, target) : undefined} />

                        <p className="sync-how__note">
                            <TbInfoCircle aria-hidden="true" />
                            <span>{t(`syncHowItWorks.${method}.note`)}</span>
                        </p>
                    </div>
                </div>
            }
        />
    );
}
